import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActionReq } from "shared/actionReq"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { FeatureEnum } from "shared/featureData"
import { Reservation } from "shared/reservation"
import { useCreateThingsMutation } from "src/features/things/thingsApi"
import useFeatures from "src/features/things/useFeatures"
import { useCurrentTenant } from "src/hooks/useCurrentTenant"
import { v4 as uuid } from "uuid"
import { parseSst2Utc } from "../sst2DateUtils"
import { Sl2Btn } from "./Sl2Btn"

/**
 * Statuses on which extending makes no sense, and which the backend rejects
 * outright (`extendReservationExpirationHandler`: "is not active").
 *
 * `EXPIRED` is deliberately NOT here — that is the whole point of LOCKER-320.
 * `hasDeposited` in `useReservation` lumps the terminal statuses in with the
 * genuinely-deposited ones, so without this list a CANCEL reservation with a
 * future expiration would render a button whose only possible outcome is a 500.
 */
const NON_EXTENDABLE_STATUSES = [
  "CANCEL",
  "CANCELLED",
  "DELETED",
  "CLOSE",
  "CLOSED",
]

export type OsExtendExpirationDialogProps = {
  reservation?: Reservation
  /**
   * The reservation's deadline has already passed. Drives the copy and the CTA
   * label only — the extension itself is re-anchored on `now` server-side.
   */
  hasExpired: boolean
  /**
   * Whether a control is rendered above this one, so the separator is drawn only
   * when it actually separates something. When the reservation has expired the
   * reminder button is gone and this is the first control in the step.
   */
  showTopDivider?: boolean
  onError: (message: string) => void
}

/**
 * The "extend expiration" control and its dialog.
 *
 * Extracted from `OsLockerReservationStepper` by LOCKER-320: the button had to
 * become reachable from a branch the stepper renders when the reservation has
 * expired, and the stepper was already 591 lines — past the ~400-line ceiling
 * in CLAUDE.md — so re-nesting it there would have made a long file longer.
 *
 * Self-gating on purpose: the feature check, the status check and the
 * "is there an expiration to extend at all" check all live here, so the parent
 * only decides *where in the lifecycle* the control belongs, never *whether the
 * tenant may see it*.
 */
export const OsExtendExpirationDialog: FC<OsExtendExpirationDialogProps> = ({
  reservation,
  hasExpired,
  showTopDivider,
  onError,
}) => {
  const { t } = useTranslation()
  const { tenant } = useCurrentTenant()
  const { canUseFeature } = useFeatures(tenant?.id)
  const [createThings] = useCreateThingsMutation()

  const [isOpen, setIsOpen] = useState(false)
  const [selectedExpiration, setSelectedExpiration] = useState("24")
  const [isCreatingActionReq, setIsCreatingActionReq] = useState(false)
  const [result, setResult] = useState<{
    success: boolean
    message: string
  } | null>(null)

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSelectedExpiration("24")
  }, [])

  const handleExtend = useCallback(async () => {
    if (!reservation?.id) {
      console.error("No reservation id")
      return
    }
    const actionReq: Partial<ActionReq> = {
      thingTypeMrn: ActionReqThingTypeMrn,
      executionMode: ActionReqExecutionModeEnum.sync,
      tenantId: reservation.tenantId,
      idempotencyKey: uuid(),
      actionType: ActionReqTypeEnum.ExtendReservationExpiration,
      inputData: {
        reservationId: reservation.id,
        extensionHours: parseInt(selectedExpiration),
      },
    }

    setIsCreatingActionReq(true)
    try {
      const res = await createThings({
        body: { things: [actionReq] },
        params: { tenantId: reservation.tenantId },
      }).unwrap()
      const resAction = res.things?.[0]
      if (resAction?.status === "success") {
        setResult({
          success: true,
          message: t("extendExpirationSuccess", {
            newExpirationDate: new Date(
              resAction.resultData.expirationTime,
            ).toLocaleString(),
          }),
        })
      } else {
        setResult({ success: false, message: t("extendExpirationError") })
        onError(t("extendExpirationError"))
      }
    } catch (e: any) {
      console.error(e)
      setResult({ success: false, message: t("extendExpirationError") })
      onError(t("extendExpirationError"))
    } finally {
      setIsCreatingActionReq(false)
      handleClose()
    }
  }, [
    createThings,
    handleClose,
    onError,
    reservation?.id,
    reservation?.tenantId,
    selectedExpiration,
    t,
  ])

  const sstReservation = reservation?.sstReservation

  if (!canUseFeature(FeatureEnum.ReservationExpirationExtension)) return null
  // Nothing to extend: the reservation has no deadline recorded.
  if (!sstReservation?.expiration_time) return null
  if (NON_EXTENDABLE_STATUSES.includes(sstReservation.status || "")) return null

  const currentExpiration = parseSst2Utc(sstReservation.expiration_time)

  return (
    <>
      {showTopDivider && (
        <Box sx={{ width: "50%" }}>
          <Divider />
        </Box>
      )}
      <Sl2Btn variant="outlined" color="primary" onClick={() => setIsOpen(true)}>
        {hasExpired ? t("reactivateAndExtend") : t("extendExpiration")}
      </Sl2Btn>
      {result?.success && (
        <Typography variant="caption" color="textSecondary">
          {result.message}
        </Typography>
      )}
      <Dialog data-ui="UI-SL2W-055" open={isOpen} onClose={handleClose}>
        <DialogTitle>
          {hasExpired ? t("reactivateAndExtend") : t("extendExpiration")}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack direction="column" spacing={1}>
            {hasExpired && (
              // The dialog otherwise shows a deadline in the past next to a
              // "+24 h" choice, which reads as "+24 h from that past date" —
              // the exact misreading the server-side clamp exists to prevent.
              <Alert severity="info">{t("extendExpirationRevivesNotice")}</Alert>
            )}
            <Typography variant="body2" color="textSecondary">
              {t("currentExpirationTime", {
                expirationTime:
                  currentExpiration?.toLocaleString() ??
                  sstReservation.expiration_time,
              })}
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {t("chooseExpirationExtension")}
              </FormLabel>
              <RadioGroup
                aria-label="expiration"
                name="expiration"
                value={selectedExpiration}
                onChange={(e) => setSelectedExpiration(e.target.value)}
              >
                <FormControlLabel
                  value="24"
                  control={<Radio />}
                  label={`24 ${t("hours")}`}
                />
                <FormControlLabel
                  value="48"
                  control={<Radio />}
                  label={`48 ${t("hours")}`}
                />
                <FormControlLabel
                  value="72"
                  control={<Radio />}
                  label={`72 ${t("hours")}`}
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Sl2Btn onClick={handleClose} color="primary">
            {t("close")}
          </Sl2Btn>
          <Sl2Btn
            onClick={handleExtend}
            color="primary"
            variant="contained"
            disabled={isCreatingActionReq}
          >
            {isCreatingActionReq && <CircularProgress size={16} />}
            {t("confirm")}
          </Sl2Btn>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OsExtendExpirationDialog
